// useAgora — wraps the Agora Web SDK for the user (business-portal) side.
// Joins a channel using credentials minted by Laravel (appId/channel/token/uid),
// publishes local mic+camera, tracks remote users, and exposes call controls.
import { useCallback, useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";

AgoraRTC.setLogLevel(2);

/**
 * @param {object|null} creds { appId, channel, token, uid } or null to stay idle.
 */
export function useAgora(creds) {
  const clientRef = useRef(null);
  const localAudioRef = useRef(null);
  const localVideoRef = useRef(null);
  const joinedRef = useRef(false);

  const [status, setStatus] = useState("idle"); // idle|connecting|connected|failed|left
  const [error, setError] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);

  useEffect(() => {
    if (!creds || !creds.appId || !creds.channel) return;
    let cancelled = false;

    const client = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
    clientRef.current = client;

    const sync = () => setRemoteUsers(Array.from(client.remoteUsers));

    client.on("user-published", async (user, mediaType) => {
      console.log("[call][user] remote user-published", user.uid, mediaType);
      await client.subscribe(user, mediaType);
      if (mediaType === "audio") user.audioTrack?.play();
      sync();
    });
    client.on("user-unpublished", sync);
    client.on("user-joined", (user) => { console.log("[call][user] remote user-joined", user.uid); sync(); });
    client.on("user-left", sync);
    client.on("connection-state-change", (cur, prev, reason) =>
      console.log("[call][user] conn-state", prev, "->", cur, reason || "")
    );

    (async () => {
      try {
        setStatus("connecting");
        console.log("[call][user] joining", {
          channel: creds.channel, uid: creds.uid, appId: creds.appId, hasToken: !!creds.token,
        });
        await client.join(creds.appId, creds.channel, creds.token || null, creds.uid);
        console.log("[call][user] JOINED channel", creds.channel, "as uid", creds.uid);
        joinedRef.current = true;
        // If we were torn down during the async join, leave so we don't hold
        // the uid (which would cause UID_CONFLICT on the next join attempt).
        if (cancelled) {
          await client.leave().catch(() => {});
          return;
        }

        const [audioTrack, videoTrack] =
          await AgoraRTC.createMicrophoneAndCameraTracks({}, { encoderConfig: "480p_1" });
        if (cancelled) {
          audioTrack.close();
          videoTrack.close();
          await client.leave().catch(() => {});
          return;
        }
        localAudioRef.current = audioTrack;
        localVideoRef.current = videoTrack;
        setLocalVideoTrack(videoTrack);

        await client.publish([audioTrack, videoTrack]);
        console.log("[call][user] PUBLISHED local tracks");
        if (!cancelled) setStatus("connected");
      } catch (err) {
        if (cancelled) return;
        console.error("[call][user] join failed", err);
        setError(err.message || "Failed to start the call.");
        setStatus("failed");
      }
    })();

    return () => {
      cancelled = true;
      const c = clientRef.current;
      localAudioRef.current?.close();
      localVideoRef.current?.close();
      localAudioRef.current = null;
      localVideoRef.current = null;
      setLocalVideoTrack(null);
      client.removeAllListeners();
      // Always attempt to leave the client we created (safe even if the join
      // is still in flight) so we never leave a stale joined client behind.
      (c || client)?.leave().catch(() => {});
      joinedRef.current = false;
      setRemoteUsers([]);
      setStatus("idle");
      setMicOn(true);
      setCamOn(true);
    };
  }, [creds]);

  const toggleMic = useCallback(async () => {
    const track = localAudioRef.current;
    if (!track) return;
    const next = !micOn;
    await track.setEnabled(next);
    setMicOn(next);
  }, [micOn]);

  const toggleCam = useCallback(async () => {
    const track = localVideoRef.current;
    if (!track) return;
    const next = !camOn;
    await track.setEnabled(next);
    setCamOn(next);
  }, [camOn]);

  return { status, error, remoteUsers, localVideoTrack, micOn, camOn, toggleMic, toggleCam };
}
